import React from 'react';
import { Link2, ListChecks, Repeat } from 'lucide-react';
import { taskRecurrenceLabel } from '../../../utils/taskRecurrence';
import { doneLabel, dueLabel, leadLabel } from '../taskViews';
import type { Task, TaskMember } from '../types';
import { PriorityPill, TONE_TEXT_CLASS, TaskAvatar, TaskCheckButton } from './taskUi';

type Props = {
  task: Task;
  assignee: TaskMember | null;
  today: string;
  selected: boolean;
  /** Tastatur-Fokus (↑/↓) */
  focused?: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onToggle: () => void;
};

export const TaskRow = React.memo(function TaskRow({
  task,
  assignee,
  today,
  selected,
  focused = false,
  canEdit,
  onSelect,
  onToggle,
}: Props) {
  // Erledigte zeigen, wann sie erledigt wurden — die Fälligkeit ist dann nicht mehr relevant.
  const due = task.done ? { text: doneLabel(task, today), tone: 'default' as const } : dueLabel(task, today);
  const checklistDone = task.checklist.filter((c) => c.done).length;
  const recurrence = task.recurrence && !task.done ? taskRecurrenceLabel(task.recurrence, task.due_date) : null;
  const hasMeta = Boolean(task.lead) || task.checklist.length > 0 || Boolean(recurrence);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      data-task-id={task.id}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onSelect();
      }}
      className={`-mx-2 flex cursor-pointer items-center gap-3 rounded-md border-b border-app-border px-2 py-[11px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
        selected ? 'bg-inbox-selected' : 'hover:bg-app-hover'
      } ${focused ? 'ring-2 ring-inset ring-pipedrive-blue/40' : ''}`}
    >
      <TaskCheckButton done={task.done} priority={task.priority} onToggle={onToggle} disabled={!canEdit} />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Mobil zwei Zeilen Titel statt abgeschnitten */}
        <span
          className={`line-clamp-2 break-words text-sm leading-snug md:truncate ${task.done ? 'text-app-text-subtle line-through' : 'text-app-text'}`}
        >
          {task.title}
        </span>
        {(hasMeta || due.text) && (
          <div className={`${hasMeta ? 'flex' : 'flex md:hidden'} min-w-0 items-center gap-3 text-xs text-app-text-subtle`}>
            {/* Mobil steht die Fälligkeit hier statt rechts, damit der Titel Platz hat */}
            {due.text && (
              <span className={`shrink-0 whitespace-nowrap font-medium md:hidden ${TONE_TEXT_CLASS[due.tone]}`}>
                {due.text}
              </span>
            )}
            {task.lead && (
              <span className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap">
                <Link2 className="h-3 w-3 shrink-0" />
                {task.lead.display_id && (
                  <span className="shrink-0 font-mono text-[11px]">{task.lead.display_id}</span>
                )}
                <span className="truncate">{leadLabel(task.lead)}</span>
              </span>
            )}
            {recurrence && (
              <span className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap" title={recurrence}>
                <Repeat className="h-3 w-3 shrink-0" />
                <span className="truncate">{recurrence}</span>
              </span>
            )}
            {task.checklist.length > 0 && (
              <span className="flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums">
                <ListChecks className="h-3 w-3" />
                {checklistDone}/{task.checklist.length}
              </span>
            )}
          </div>
        )}
      </div>

      {due.text && (
        <span className={`hidden shrink-0 whitespace-nowrap text-xs font-medium md:inline ${TONE_TEXT_CLASS[due.tone]}`}>
          {due.text}
        </span>
      )}
      <span className="hidden shrink-0 sm:inline-flex">
        <PriorityPill priority={task.priority} />
      </span>
      <TaskAvatar member={assignee} />
    </div>
  );
});
